import { useAppDispatch, useAppSelector } from 'app/hooks';
import AccessorySelectionModal from 'components/layout/modals/accessory-selection-modal';
import {
  clearDuctedPairPick,
  clearDuctedPairPicks,
  revealDuctedTail,
  setActiveTab,
  setDuctedPagination,
  setDuctedPairPick
} from 'features/ui';
import { useComparisonSelection } from 'hooks/use-comparison-selection';
import useDuctedCombinationAdd from 'hooks/use-ducted-combination-add';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  isPickedForPair,
  selectDuctedBundleMode,
  selectDuctedBundleSlots
} from 'utils/ducted-bundle-picks';
import {
  compareStateForCard,
  withMappedComparedId
} from 'utils/ducted-compare-state';
import { syncDuctedPairPick } from 'utils/ducted-pair-pick-sync';
import {
  DuctedCompareSelectRequest,
  findSelectionForComparedSystem,
  isDuctedRailSectionSkipped,
  rankGroupCardSelection
} from 'utils/ducted-rail-selections';
import { unevaluatedFilterLabels } from 'utils/ducted-ratings';
import {
  ductedDuplicateNameIndexes,
  numberedDuctedSystemLabel
} from 'utils/duplicate-system-names';
import {
  hasDuctedTonnageFilter,
  isNoCoolingSideCombination
} from './ducted-rail-sections';
import { pairSelectLabels } from './compare-action-text';
import { DuctedRailSectionModel } from './ducted-rail-sections-types';
import { DuctedCombinationCardProps } from './ducted-combination-card';
import DuctedFeaturedSystemCard from './ducted-featured-system-card';
import DuctedRailSectionHeading from './ducted-rail-section-heading';
import {
  buildDuctedSectionItems,
  ductedSectionHasRowsBeyondLeads,
  DuctedRailItem,
  DuctedRailPlaceholderItem
} from './ducted-rail-section-items';
import { DuctedSectionDisplay } from './ducted-section-display-types';
import { DuctedPanelSelection } from './ducted-system-detail-panel';
import { ductedSectionDisplay } from './ducted-system-list-loading';
import DuctedSystemSkeletonCard from './ducted-system-skeleton-card';
import useDuctedRailSections from './use-ducted-rail-sections';

// Systems column for the ducted pathway on Choose Systems (AHA-6681 CS-2):
// one rank group per recommended system type. Until "Show more" is clicked
// the rail shows each type's lead cards — the first row of the type in each
// result set, in the server's "Sort by" order (the best fit under
// Recommended, the type's best figure under a metric sort) — grouped by
// type, with the kept-pairing lead (when the type pairs with the equipment
// the homeowner keeps) ahead of the full-replacement lead. Under Recommended
// the groups follow the Recommended-for-you order the Equipment Preferences
// step shows; under a metric sort they follow the server's order of their
// leads. "Show more" reveals the loaded rows past those leads
// (ducted-rail-section-items) and then pages. Card
// selection is controlled by the view (AHA-6681 CS-3): the highlight
// follows `selectedCardId` and clicks lift the full DuctedPanelSelection
// payload to `onSelectCard`, which feeds the ducted detail panel.
type DuctedSystemsRailProps = Readonly<{
  selectedCardId: string | null;
  onSelectCard: (selection: DuctedPanelSelection | null) => void;
  onInspectCard?: (selection: DuctedPanelSelection) => void;
  comparedIdByCardId?: ReadonlyMap<string, string>;
  onComparedIdMapped?: (
    cardId: string,
    systemId: string,
    selection: DuctedPanelSelection
  ) => void;
  selectComparedRequest?: DuctedCompareSelectRequest | null;
}>;

// First selectable card in exact render order (AHA-6681 auto-select):
// sections in eligible-system order, rank groups in recommendation order,
// kept card before the full-replacement card. Only representatives are
// considered — every reachable null-selection state has all tails collapsed
// (initial mount, filter reset, pathway flip), so a tail row could never be
// the first VISIBLE card at auto-select time. Only a settled ('loaded'),
// current entry may supply the selection: entries flip to 'loading' the
// moment a refetch begins, which is what keeps a filter change from
// re-selecting a stale card out of the previous result set. The
// representative itself must also be settled: a refetching rep keeps its
// PREVIOUS combination under status 'loading', and auto-selecting that
// snapshot leaves the panel showing the old combination after the card
// re-renders with the new one.
const deriveFirstSelection = (
  sections: DuctedRailSectionModel[]
): DuctedPanelSelection | null => {
  for (const section of sections) {
    if (!isDuctedRailSectionSkipped(section)) {
      for (const group of section.rankGroups) {
        const firstSelectable = group.cards.find(
          (card) =>
            card.entry.status === 'loaded' &&
            card.representative?.status === 'loaded' &&
            card.representative.combination != null
        );
        const selection = firstSelectable
          ? rankGroupCardSelection(section, firstSelectable)
          : null;
        if (selection) return selection;
      }
    }
  }
  return null;
};

// Current payload for a specific representative cardId, or null when that
// card has no settled data right now. Backs the selection self-heal: results
// can be replaced WITHOUT a filter-signature change (the EE pre-warm fetches
// land unfiltered before the tonnage chain sets its filter, then the view
// mounts and refetches filtered), leaving the lifted payload pointing at a
// combination the card no longer renders.
const deriveSelectionForCard = (
  sections: DuctedRailSectionModel[],
  cardId: string
): DuctedPanelSelection | null => {
  for (const section of sections) {
    for (const group of section.rankGroups) {
      const match = group.cards.find(
        (card) =>
          card.entry.status === 'loaded' &&
          card.representative?.status === 'loaded' &&
          card.representative.combination != null &&
          `${section.system.uniqueId}:${card.resultSet}:rep:${card.systemType.code}` ===
            cardId
      );
      const selection = match ? rankGroupCardSelection(section, match) : null;
      if (selection) return selection;
    }
  }
  return null;
};

export default function DuctedSystemsRail({
  selectedCardId,
  onSelectCard,
  onInspectCard,
  comparedIdByCardId: comparedIdByCardIdProp,
  onComparedIdMapped,
  selectComparedRequest = null
}: DuctedSystemsRailProps) {
  const dispatch = useAppDispatch();
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const ductedFilters = useAppSelector((state) => state.ui.ductedFilters);
  const { tailRevealed } = ductedFilters;
  const systemSort = useAppSelector((state) => state.ui.systemSort);
  const inStockOnly = useAppSelector(
    (state) => state.ui.galleryFilters.inStock
  );
  const priceMin = useAppSelector((state) => state.ui.galleryFilters.priceMin);
  const priceMax = useAppSelector((state) => state.ui.galleryFilters.priceMax);
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const { addSystemsToComparison, removeSystem } = useComparisonSelection();
  const bundleMode = useAppSelector(selectDuctedBundleMode);
  const bundleSlots = useAppSelector(selectDuctedBundleSlots);
  const ductedPairPicks = useAppSelector((state) => state.ui.ductedPairPicks);
  const bundleSlotFor = (systemUniqueId: string) =>
    bundleMode
      ? bundleSlots.find((slot) => slot.systemUniqueId === systemUniqueId) ??
        null
      : null;

  // Card → added-system map backing the toggle: a card is "Added" while its
  // mapped system is still in systemsToCompare, so removals via the
  // ComparisonTabs X self-clear the card. Keyed by cardId (per-card
  // identity), so duplicate combination IDs across systems and result sets
  // stay independent. When the view lifts the map, panel-initiated adds
  // flip the matching card. Never pruned on remove: the added state
  // re-derives from systemsToCompare.
  const [internalComparedIdByCardId, setInternalComparedIdByCardId] = useState<
    ReadonlyMap<string, string>
  >(new Map());
  const comparedIdByCardId =
    comparedIdByCardIdProp ?? internalComparedIdByCardId;

  const { requestAdd, accessorySelection } = useDuctedCombinationAdd({
    onAdded: (systems, context) => {
      // Panel parity: every added system goes into the comparison, and the
      // clicked card is mapped to its manufactured system for the toggle's
      // added-state derivation.
      addSystemsToComparison(systems);
      const addedId = systems[0]?.id;
      if (addedId == null) return;
      if (onComparedIdMapped) {
        onComparedIdMapped(context.cardId, addedId, context);
      } else {
        setInternalComparedIdByCardId((previous) =>
          withMappedComparedId(previous, context.cardId, addedId)
        );
      }
    }
  });

  const pendingCompatibilityAddIds = useAppSelector(
    (state) => state.systems.pendingCompatibilityAddIds
  );
  const isAddingSelection = (selection: DuctedPanelSelection) =>
    pendingCompatibilityAddIds.includes(Number(selection.combination.ID));

  const nameIndexes = useMemo(
    () => ductedDuplicateNameIndexes(systemsToCompare),
    [systemsToCompare]
  );

  const compareStateFor = (selection: DuctedPanelSelection) =>
    compareStateForCard(comparedIdByCardId, systemsToCompare, selection);

  const numberedCardLabel = (
    label: string,
    selection: DuctedPanelSelection
  ) => {
    const { comparedId, added } = compareStateFor(selection);
    if (!added || comparedId == null) return label;
    return numberedDuctedSystemLabel(
      label,
      systemsToCompare.find((system) => system.id === comparedId),
      nameIndexes
    );
  };

  const handleToggleCompare = (context: DuctedPanelSelection) => {
    if (isAddingSelection(context)) return;
    if (bundleSlotFor(context.systemUniqueId)) {
      if (
        isPickedForPair(ductedPairPicks, context.systemUniqueId, context.cardId)
      ) {
        dispatch(clearDuctedPairPick(context.systemUniqueId));
      } else {
        dispatch(
          setDuctedPairPick({
            systemUniqueId: context.systemUniqueId,
            pick: { selection: context }
          })
        );
      }
      return;
    }
    const { comparedId, added } = compareStateFor(context);
    if (added && comparedId !== undefined) {
      // Same pathway as the ComparisonTabs X: tray + selection + server row.
      removeSystem(comparedId);
      return;
    }
    requestAdd(context);
  };

  const tonnageFilterActive = hasDuctedTonnageFilter(ductedFilters.filters);
  const isNoCoolingSide = (combination: Record<string, unknown>) =>
    isNoCoolingSideCombination(combination, tonnageFilterActive);

  // Selection reset on a result-set change (CCB clear-on-filter-change
  // spirit): a filter, chip, fuel or sort change re-orders the rail and can
  // change every lead card, so the selection starts over from the new first
  // card. Filter/code/sort changes already reset the paging via the CS-1
  // reducers; inStockOnly and the price bounds have no reducer-side reset,
  // so re-assert page 1 here (a no-op dispatch when already there).
  const filtersSignature = useMemo(
    () =>
      JSON.stringify([
        ductedFilters.filters,
        ductedFilters.systemTypeSelections,
        ductedFilters.fuelSelections,
        ductedFilters.equipmentMatchingOnly,
        inStockOnly,
        priceMin,
        priceMax,
        systemSort
      ]),
    [
      ductedFilters.filters,
      ductedFilters.systemTypeSelections,
      ductedFilters.fuelSelections,
      ductedFilters.equipmentMatchingOnly,
      inStockOnly,
      priceMin,
      priceMax,
      systemSort
    ]
  );
  // Every selection write goes through liftSelection so lastLiftedRef always
  // mirrors the payload the detail panel currently holds — the self-heal
  // effect below compares fresh data against it.
  const lastLiftedRef = useRef<DuctedPanelSelection | null>(null);
  const liftSelection = useCallback(
    (selection: DuctedPanelSelection | null) => {
      lastLiftedRef.current = selection;
      onSelectCard(selection);
    },
    [onSelectCard]
  );
  const inspectSelection = useCallback(
    (selection: DuctedPanelSelection) => {
      liftSelection(selection);
      onInspectCard?.(selection);
    },
    [liftSelection, onInspectCard]
  );

  const previousSignatureRef = useRef(filtersSignature);
  useEffect(() => {
    if (previousSignatureRef.current === filtersSignature) return;
    previousSignatureRef.current = filtersSignature;
    liftSelection(null);
    dispatch(clearDuctedPairPicks());
    dispatch(setDuctedPagination({ pageNumber: 1 }));
  }, [filtersSignature, dispatch, liftSelection]);

  const { eligibleSystems, sections } = useDuctedRailSections();

  useEffect(() => {
    Object.entries(ductedPairPicks).forEach(([systemUniqueId, pick]) => {
      const synced = syncDuctedPairPick(
        pick,
        deriveSelectionForCard(sections, pick.selection.cardId)
      );
      if (synced !== null) {
        dispatch(setDuctedPairPick({ systemUniqueId, pick: synced }));
      }
    });
  }, [sections, ductedPairPicks, dispatch]);

  const firstSelection = useMemo(
    () => deriveFirstSelection(sections),
    [sections]
  );

  // Auto-select the first rendered card (AHA-6681): fires only when nothing
  // is selected — initial load once results land, and after the
  // filter-signature effect above clears the selection. Firing lifts the
  // selection, so the selectedCardId guard blocks any re-run; an existing
  // selection is never overridden.
  useEffect(() => {
    if (selectedCardId !== null || firstSelection === null) return;
    liftSelection(firstSelection);
  }, [selectedCardId, firstSelection, liftSelection]);

  // Selection self-heal: when the selected representative card's data is
  // replaced WITHOUT a filter-signature change (pre-warm results superseded
  // by the view's own filtered refetch, rep refresh after a context settle),
  // the card re-renders with the new combination while the panel still holds
  // the lifted snapshot — re-lift the fresh payload for the same card. Fires
  // only when the settled combination ID actually differs from what was
  // lifted, so it cannot loop and never touches manual choices of a card
  // that still shows the same combination.
  useEffect(() => {
    if (selectedCardId === null) return;
    const last = lastLiftedRef.current;
    if (last === null || last.cardId !== selectedCardId) return;
    const current = deriveSelectionForCard(sections, selectedCardId);
    if (current !== null && current.combination.ID !== last.combination.ID) {
      liftSelection(current);
    }
  }, [sections, selectedCardId, liftSelection]);

  const consumedSelectRequestNonceRef = useRef(0);
  useEffect(() => {
    if (
      selectComparedRequest === null ||
      selectComparedRequest.nonce === consumedSelectRequestNonceRef.current
    ) {
      return;
    }
    const comparedSystem = systemsToCompare.find(
      (system) => system.id === selectComparedRequest.systemId
    );
    const match = comparedSystem
      ? findSelectionForComparedSystem(
          sections,
          comparedIdByCardId,
          comparedSystem
        )
      : null;
    if (match === null) return;
    consumedSelectRequestNonceRef.current = selectComparedRequest.nonce;
    inspectSelection(match.selection);
  }, [
    selectComparedRequest,
    sections,
    systemsToCompare,
    comparedIdByCardId,
    inspectSelection
  ]);

  // Each visible section with the one thing its grid shows right now
  // (ductedSectionDisplay): the initial skeletons, the refresh skeletons, or
  // the cards in the server's order.
  const visibleSections = sections
    .filter(
      (section) =>
        section.subSections.length > 0 && !isDuctedRailSectionSkipped(section)
    )
    .map((section) => ({
      section,
      display: ductedSectionDisplay(
        section,
        buildDuctedSectionItems(section, systemSort, tailRevealed),
        ductedFilters.pageSize
      )
    }));
  const allSubSections = sections.flatMap((section) => section.subSections);
  const serverHasMore = allSubSections.some(
    (subSection) =>
      (subSection.entry?.combinations.length ?? 0) <
      (subSection.entry?.totalCount ?? 0)
  );
  const isLoadingMore = allSubSections.some(
    (subSection) => subSection.entry?.status === 'loading'
  );
  // While any section shows skeletons they already say a page is on its
  // way; a "Show more" button under them would be about rows that are
  // going away.
  const anySectionShowingSkeletons = visibleSections.some(
    ({ display }) => display.kind !== 'items'
  );
  // "Show more" first reveals the loaded rows past each type's leads, then
  // pages the server's order.
  const hasRowsToReveal =
    !tailRevealed && sections.some(ductedSectionHasRowsBeyondLeads);
  const showMoreAvailable =
    !anySectionShowingSkeletons && (hasRowsToReveal || serverHasMore);

  // Skips are recorded for every result set in play together, so the
  // always-present alternatives entry is the honest per-system signal.
  const allSkipped =
    sections.length > 0 && sections.every(isDuctedRailSectionSkipped);
  // Copy variant for the all-skipped empty state (AHA-6681 want-only
  // fallback): with wants saved, an all-skipped rail only occurs when every
  // selected want type is unmapped (no compatibility codes) — steering the
  // user to "choose what you want" would be wrong, they already did.
  const anyWantsSaved = eligibleSystems.some(
    (system) =>
      (recommendationSelectionsBySystem[system.uniqueId]?.wantIds?.length ??
        0) > 0
  );
  if (eligibleSystems.length === 0) return null;

  const handleLoadMore = () => {
    if (!tailRevealed) {
      dispatch(revealDuctedTail());
      return;
    }
    dispatch(setDuctedPagination({ pageNumber: ductedFilters.pageNumber + 1 }));
  };

  const renderSystemCard = (
    selection: DuctedPanelSelection,
    productData: DuctedCombinationCardProps['productData'],
    accessoryData: DuctedCombinationCardProps['accessoryData']
  ) => {
    const bundleSlot = bundleSlotFor(selection.systemUniqueId);
    const picked =
      bundleSlot !== null &&
      isPickedForPair(
        ductedPairPicks,
        selection.systemUniqueId,
        selection.cardId
      );
    return (
      <DuctedFeaturedSystemCard
        combination={selection.combination}
        productData={productData}
        accessoryData={accessoryData}
        modelSpecs={selection.modelSpecs}
        title={numberedCardLabel(selection.title, selection)}
        typeLabel={
          selection.typeLabel
            ? numberedCardLabel(selection.typeLabel, selection)
            : selection.typeLabel
        }
        keptPairing={selection.keptPairing}
        noCoolingSide={isNoCoolingSide(selection.combination)}
        unevaluatedFilterLabels={unevaluatedFilterLabels(selection.combination)}
        isSelected={false}
        onSelect={() => inspectSelection(selection)}
        isAddedToCompare={
          bundleSlot ? picked : compareStateFor(selection).added
        }
        isAdding={isAddingSelection(selection)}
        onToggleCompare={() => handleToggleCompare(selection)}
        compareLabels={
          bundleSlot ? pairSelectLabels(bundleSlot.pairName) : undefined
        }
      />
    );
  };

  const renderPlaceholder = (item: DuctedRailPlaceholderItem) => (
    <div key={item.key} data-testid='ducted-rank-group' className='min-w-0'>
      {item.status === 'loading' ? (
        <DuctedSystemSkeletonCard />
      ) : (
        <div className='text-xs text-red-600'>
          Couldn&apos;t load {item.systemType.label}
        </div>
      )}
    </div>
  );

  const renderItem = (item: DuctedRailItem) => {
    if (item.kind === 'placeholder') return renderPlaceholder(item);
    return (
      <div
        key={item.key}
        data-testid={item.featured ? 'ducted-rank-group' : undefined}
        className='min-w-0'
      >
        {renderSystemCard(item.selection, item.productData, item.accessoryData)}
      </div>
    );
  };

  const renderDisplay = (display: DuctedSectionDisplay) => {
    switch (display.kind) {
      case 'awaiting':
        return display.skeletonKeys.map((key) => (
          <DuctedSystemSkeletonCard key={key} />
        ));
      case 'refreshing':
        return Array.from({ length: display.skeletonCount }, (_, index) => (
          <DuctedSystemSkeletonCard key={`refresh-${index}`} />
        ));
      default:
        return display.items.map(renderItem);
    }
  };

  const renderSection = ({
    section,
    display
  }: {
    section: DuctedRailSectionModel;
    display: DuctedSectionDisplay;
  }) => {
    const { system, hasKept, subSections, rankGroups } = section;
    const keptEntry = subSections.find(
      (subSection) => subSection.resultSet === 'kept'
    )?.entry;
    const keptEmpty =
      hasKept && keptEntry?.status === 'loaded' && keptEntry.totalCount === 0;
    const erroredSubSections = subSections.filter(
      (subSection) => subSection.entry?.status === 'error'
    );
    const scopedEmpty =
      rankGroups.length === 0 &&
      subSections.some((subSection) => subSection.scopedEmpty);
    const loading = display.kind !== 'items';
    return (
      <div key={system.uniqueId} className='flex flex-col gap-3'>
        <DuctedRailSectionHeading
          systemUniqueId={system.uniqueId}
          bundleSlot={bundleSlotFor(system.uniqueId)}
        />
        {erroredSubSections.map((subSection) => (
          <div key={subSection.resultSet} className='text-xs text-red-600'>
            Couldn&apos;t load compatible systems
          </div>
        ))}
        {scopedEmpty && !loading && (
          <div className='text-xs text-gray-500'>
            No systems of the selected types in this group
          </div>
        )}
        {keptEmpty && (
          <div className='flex flex-col items-start gap-1 rounded-lg border border-gray-200 bg-white p-3'>
            <span className='text-xs text-gray-600'>
              No systems pair with the equipment you&apos;re keeping
            </span>
            <button
              type='button'
              className='text-xs font-medium text-blue-600 hover:text-blue-700'
              onClick={() => dispatch(setActiveTab('existingEquipment'))}
            >
              Review your equipment on the Existing Equipment step
            </button>
          </div>
        )}
        {loading && (
          <span className='sr-only' role='status'>
            Loading systems
          </span>
        )}
        <div
          data-testid='ducted-featured-grid'
          aria-busy={loading}
          className='grid min-w-0 gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,22rem),1fr))]'
        >
          {renderDisplay(display)}
        </div>
      </div>
    );
  };

  return (
    <div className='flex min-w-0 flex-col gap-3 overflow-x-hidden'>
      {/* Wants-gateway empty state (AHA-6681 sub-build 3): every eligible
          system was skipped — no wants selected or nothing recommended.
          The CTA steers back to the Existing Equipment step, where the
          recommendations strip owns the wants selection. */}
      {allSkipped && (
        <div className='flex flex-col items-start gap-2 rounded-lg border border-gray-200 bg-white p-4'>
          <span className='text-sm font-semibold text-gray-700'>
            No recommended systems yet
          </span>
          <span className='text-xs text-gray-500'>
            {anyWantsSaved
              ? "Your selected system types don't have searchable systems yet — adjust what you want on the Existing Equipment step."
              : "Choose what you want on the Existing Equipment step and we'll recommend compatible systems here."}
          </span>
          <button
            type='button'
            className='rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700'
            onClick={() => dispatch(setActiveTab('existingEquipment'))}
          >
            Go to Existing Equipment
          </button>
        </div>
      )}
      {visibleSections.map(renderSection)}
      {showMoreAvailable && (
        <button
          type='button'
          disabled={isLoadingMore}
          className='inline-flex items-center justify-center gap-1 self-center px-2 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-400'
          onClick={handleLoadMore}
        >
          {isLoadingMore ? 'Loading more...' : 'Show more'}
        </button>
      )}
      {/* Multi-option accessory parity with the detail panel: the hook opens
          this when the clicked combination carries >1 option for the same
          accessory type. Exactly one instance for the whole rail. */}
      <AccessorySelectionModal
        openState={accessorySelection.openState}
        combinationsWithChoices={accessorySelection.combinationsWithChoices}
        systemCount={accessorySelection.systemCount}
        onConfirm={accessorySelection.onConfirm}
      />
    </div>
  );
}
